import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Bell, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPush() {
  const { authFetch } = useAdmin();
  const [stats, setStats] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/admin/push/stats`);
    if (r.ok) setStats(await r.json());
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  async function broadcast() {
    if (!title.trim() || !body.trim()) { toast.error('Naslov i tekst su obavezni'); return; }
    setSending(true);
    try {
      const r = await authFetch(`${API}/admin/push/broadcast`, {
        method: 'POST',
        body: JSON.stringify({ title, body, url: url || null }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Greška');
      }
      const d = await r.json();
      setLastResult(d);
      toast.success(`Poslano: ${d.sent} · Neuspješno: ${d.failed}`);
      setTitle(''); setBody(''); setUrl('');
      load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setSending(false); }
  }

  return (
    <div className="p-6 text-zinc-100 max-w-3xl" data-testid="admin-push-page">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2"><Bell /> Push obavijesti</h1>

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-5">
        <div className="text-sm text-zinc-400 mb-1">Pretplatnika</div>
        <div className="text-3xl font-bold text-white" data-testid="push-subscribers">{stats?.subscribers ?? '—'}</div>
        <div className="text-xs text-zinc-500 mt-2">
          VAPID: {stats?.vapid_configured ? '✓ konfiguriran' : '✗ NIJE konfiguriran'}
        </div>
        {stats?.last_broadcast && (
          <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
            Zadnja: <strong className="text-zinc-300">{stats.last_broadcast.title}</strong> —
            poslano {stats.last_broadcast.sent}, neuspjeh {stats.last_broadcast.failed}
          </div>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-3">
        <h2 className="font-medium">Novo obavještenje</h2>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Naslov"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
          data-testid="push-title" maxLength={80} />
        <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Tekst (kratak)"
          rows={3} className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
          data-testid="push-body" maxLength={200} />
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="URL (neobavezno)"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
          data-testid="push-url" />
        <div className="flex justify-end">
          <button onClick={broadcast} disabled={sending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded text-sm inline-flex items-center gap-2"
            data-testid="push-send-btn">
            <Send size={14} /> {sending ? 'Slanje…' : 'Pošalji svima'}
          </button>
        </div>
      </div>

      {lastResult && (
        <div className="mt-4 bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-xs">
          <div className="text-zinc-400 mb-1">Zadnji rezultat</div>
          <div>Poslano: <strong>{lastResult.sent}</strong> · Neuspjeh: <strong>{lastResult.failed}</strong></div>
          {lastResult.errors?.length > 0 && (
            <details className="mt-2 text-zinc-500"><summary>Greške ({lastResult.errors.length})</summary>
              <ul className="list-disc pl-5 mt-1">{lastResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
